import React, { useState } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  Tooltip,
  Link,
  Divider,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import {
  PhoneIcon,
  ExternalLinkIcon,
  StarIcon,
  CalendarIcon,
  ChevronDownIcon,
  EditIcon,
  CheckIcon,
  CloseIcon,
} from "@chakra-ui/icons";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import ContactModal from "../ContactModal";
import NegotiationModal from "../NegotiationModal";
import ConversionModal from "../ConversionModal";
import LostModal from "../LostModal";

const ProspectPlaceCard = ({ place, listId, onUpdate }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const {
    isOpen: isContactModalOpen,
    onOpen: onContactModalOpen,
    onClose: onContactModalClose,
  } = useDisclosure();

  const {
    isOpen: isNegotiationModalOpen,
    onOpen: onNegotiationModalOpen,
    onClose: onNegotiationModalClose,
  } = useDisclosure();

  const {
    isOpen: isConversionModalOpen,
    onOpen: onConversionModalOpen,
    onClose: onConversionModalClose,
  } = useDisclosure();

  const {
    isOpen: isLostModalOpen,
    onOpen: onLostModalOpen,
    onClose: onLostModalClose,
  } = useDisclosure();

  const {
    isOpen: isDetailsModalOpen,
    onOpen: onDetailsModalOpen,
    onClose: onDetailsModalClose,
  } = useDisclosure();

  const getStatusColor = () => {
    const colors = {
      pending: "gray",
      contacted: "blue",
      in_negotiation: "yellow",
      converted: "green",
      lost: "red",
    };
    return colors[place.status] || "gray";
  };

  const getStatusText = () => {
    const texts = {
      pending: "Pendente",
      contacted: "Contactado",
      in_negotiation: "Em Negociação",
      converted: "Convertido",
      lost: "Perdido",
    };
    return texts[place.status] || "Desconhecido";
  };

  const getContactChannelIcon = (channel) => {
    switch (channel) {
      case "phone":
        return "📞";
      case "whatsapp":
        return "📱";
      case "email":
        return "📧";
      case "visit":
        return "🚪";
      case "social_media":
        return "📱";
      default:
        return "💬";
    }
  };

  const getContactChannelText = (channel) => {
    const texts = {
      phone: "Telefone",
      whatsapp: "WhatsApp",
      email: "E-mail",
      visit: "Visita",
      social_media: "Redes Sociais",
      other: "Outros",
    };
    return texts[channel] || "Outros";
  };

  const handleUpdate = async (updateData) => {
    setIsUpdating(true);
    try {
      await onUpdate(place.id, updateData);
    } finally {
      setIsUpdating(false);
    }
  };

  const isNeedingReturn = place.should_return_contact && place.return_contact_date;
  const isReturnOverdue = isNeedingReturn && new Date(place.return_contact_date) < new Date();

  return (
    <>
      <Card
        shadow="md"
        _hover={{
          shadow: "lg",
          transform: "translateY(-2px)",
          transition: "all 0.2s",
        }}
        position="relative"
        overflow="hidden"
        cursor="pointer"
        onClick={onDetailsModalOpen}
      >
        {/* Status Indicator */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="4px"
          bg={`${getStatusColor()}.500`}
        />

        <CardHeader pb={2}>
          <HStack justify="space-between" align="start">
            <VStack align="start" spacing={1} flex={1}>
              <Text fontWeight="bold" fontSize="md" noOfLines={1}>
                {place.title}
              </Text>
              <Text color="gray.600" fontSize="sm" noOfLines={2}>
                {place.address}
              </Text>
            </VStack>
            <Badge colorScheme={getStatusColor()} size="sm">
              {getStatusText()}
            </Badge>
          </HStack>
        </CardHeader>

        <CardBody py={3}>
          <VStack spacing={3} align="stretch">
            {/* Informações Básicas */}
            <HStack spacing={4} wrap="wrap">
              {place.phone_number && (
                <HStack spacing={1}>
                  <Icon as={PhoneIcon} boxSize={3} color="gray.500" />
                  <Text fontSize="xs" color="gray.600">
                    {place.phone_number}
                  </Text>
                </HStack>
              )}

              {place.rating && (
                <HStack spacing={1}>
                  <Icon as={StarIcon} boxSize={3} color="yellow.500" />
                  <Text fontSize="xs" color="gray.600">
                    {place.rating} ({place.rating_count || 0})
                  </Text>
                </HStack>
              )}

              {place.website && (
                <Link href={place.website} isExternal fontSize="xs" color="blue.500">
                  <HStack spacing={1}>
                    <ExternalLinkIcon />
                    <Text>Site</Text>
                  </HStack>
                </Link>
              )}
            </HStack>

            {/* Informações de Contato */}
            {place.contacted && (
              <VStack spacing={2} align="stretch" p={3} bg="blue.50" borderRadius="md">
                <Text fontSize="sm" fontWeight="semibold" color="blue.700">
                  Informações de Contato
                </Text>

                <HStack justify="space-between">
                  <Text fontSize="xs" color="gray.600">Pessoa:</Text>
                  <Text fontSize="xs" fontWeight="medium">{place.contact_person_name}</Text>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="xs" color="gray.600">Canal:</Text>
                  <HStack>
                    <Text fontSize="xs">{getContactChannelIcon(place.contact_channel)}</Text>
                    <Text fontSize="xs" fontWeight="medium">
                      {getContactChannelText(place.contact_channel)}
                    </Text>
                  </HStack>
                </HStack>

                {place.contact_notes && (
                  <VStack align="start" spacing={1}>
                    <Text fontSize="xs" color="gray.600">Notas:</Text>
                    <Text fontSize="xs" color="gray.700" bg="white" p={2} borderRadius="md">
                      {place.contact_notes}
                    </Text>
                  </VStack>
                )}
              </VStack>
            )}

            {/* Alerta de Retorno */}
            {isNeedingReturn && (
              <VStack
                spacing={2}
                align="stretch"
                p={3}
                bg={isReturnOverdue ? "red.50" : "orange.50"}
                borderRadius="md"
                border="1px"
                borderColor={isReturnOverdue ? "red.200" : "orange.200"}
              >
                <HStack justify="space-between">
                  <Text fontSize="sm" fontWeight="semibold" color={isReturnOverdue ? "red.700" : "orange.700"}>
                    {isReturnOverdue ? "🚨 Retorno Atrasado" : "⏰ Retorno Agendado"}
                  </Text>
                  <Badge colorScheme={isReturnOverdue ? "red" : "orange"} size="sm">
                    {format(new Date(place.return_contact_date), "dd/MM/yyyy", { locale: ptBR })}
                  </Badge>
                </HStack>
              </VStack>
            )}

            {/* Informações de Conversão */}
            {place.converted && (
              <VStack spacing={2} align="stretch" p={3} bg="green.50" borderRadius="md">
                <Text fontSize="sm" fontWeight="semibold" color="green.700">
                  🎉 Conversão Realizada
                </Text>

                {place.conversion_value && (
                  <HStack justify="space-between">
                    <Text fontSize="xs" color="gray.600">Valor:</Text>
                    <Text fontSize="sm" fontWeight="bold" color="green.600">
                      R$ {place.conversion_value && typeof place.conversion_value === 'number' ? place.conversion_value.toFixed(2) : '0,00'}
                    </Text>
                  </HStack>
                )}

                {place.conversion_date && (
                  <HStack justify="space-between">
                    <Text fontSize="xs" color="gray.600">Data:</Text>
                    <Text fontSize="xs" fontWeight="medium">
                      {format(new Date(place.conversion_date), "dd/MM/yyyy", { locale: ptBR })}
                    </Text>
                  </HStack>
                )}

                {place.conversion_notes && (
                  <VStack align="start" spacing={1}>
                    <Text fontSize="xs" color="gray.600">Notas:</Text>
                    <Text fontSize="xs" color="gray.700" bg="white" p={2} borderRadius="md">
                      {place.conversion_notes}
                    </Text>
                  </VStack>
                )}
              </VStack>
            )}

            {/* Informações de Perda */}
            {place.status === "lost" && place.lost_reason && (
              <VStack spacing={2} align="stretch" p={3} bg="red.50" borderRadius="md">
                <Text fontSize="sm" fontWeight="semibold" color="red.700">
                  ❌ Motivo da Perda
                </Text>
                <Text fontSize="xs" color="gray.700" bg="white" p={2} borderRadius="md">
                  {place.lost_reason}
                </Text>
              </VStack>
            )}
          </VStack>
        </CardBody>

        <CardFooter pt={2}>
          <HStack spacing={2} width="100%">
            {/* Ações baseadas no status */}
            {place.status === "pending" && (
              <Button
                leftIcon={<PhoneIcon />}
                colorScheme="blue"
                size="sm"
                flex={1}
                onClick={(e) => {
                  e.stopPropagation();
                  onContactModalOpen();
                }}
                isLoading={isUpdating}
              >
                Registrar Contato
              </Button>
            )}

            {place.status === "contacted" && (
              <HStack spacing={2} width="100%">
                <Button
                  colorScheme="green"
                  size="sm"
                  flex={1}
                  onClick={(e) => {
                    e.stopPropagation();
                    onNegotiationModalOpen();
                  }}
                  isLoading={isUpdating}
                >
                  Iniciar Negociação
                </Button>
                <Button
                  colorScheme="red"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onLostModalOpen();
                  }}
                  isLoading={isUpdating}
                >
                  Perdido
                </Button>
              </HStack>
            )}

            {place.status === "in_negotiation" && (
              <HStack spacing={2} width="100%">
                <Button
                  leftIcon={<CheckIcon />}
                  colorScheme="green"
                  size="sm"
                  flex={1}
                  onClick={(e) => {
                    e.stopPropagation();
                    onConversionModalOpen();
                  }}
                  isLoading={isUpdating}
                >
                  Convertido
                </Button>
                <Button
                  leftIcon={<CloseIcon />}
                  colorScheme="red"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onLostModalOpen();
                  }}
                  isLoading={isUpdating}
                >
                  Perdido
                </Button>
              </HStack>
            )}

            {(place.status === "converted" || place.status === "lost") && (
              <Button
                leftIcon={<EditIcon />}
                variant="outline"
                size="sm"
                flex={1}
                onClick={(e) => {
                  e.stopPropagation();
                  onContactModalOpen();
                }}
                isLoading={isUpdating}
              >
                Editar
              </Button>
            )}
          </HStack>
        </CardFooter>
      </Card>

      {/* Modals */}
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={onContactModalClose}
        place={place}
        onUpdate={handleUpdate}
      />

      <NegotiationModal
        isOpen={isNegotiationModalOpen}
        onClose={onNegotiationModalClose}
        place={place}
        onUpdate={handleUpdate}
      />

      <ConversionModal
        isOpen={isConversionModalOpen}
        onClose={onConversionModalClose}
        place={place}
        onUpdate={handleUpdate}
      />

      <LostModal
        isOpen={isLostModalOpen}
        onClose={onLostModalClose}
        place={place}
        onUpdate={handleUpdate}
      />

      {/* Modal de Detalhes Completos */}
      <Modal isOpen={isDetailsModalOpen} onClose={onDetailsModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text fontSize="lg" fontWeight="bold">{place.title}</Text>
            <Badge colorScheme={getStatusColor()} ml={2}>{getStatusText()}</Badge>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* Informações Básicas */}
              <Box>
                <Text fontWeight="semibold" mb={2}>📍 Localização</Text>
                <Text fontSize="sm" color="gray.600" mb={2}>{place.address}</Text>

                <HStack spacing={4} wrap="wrap">
                  {place.phone_number && (
                    <HStack spacing={1}>
                      <PhoneIcon boxSize={3} color="gray.500" />
                      <Text fontSize="sm">{place.phone_number}</Text>
                    </HStack>
                  )}

                  {place.rating && (
                    <HStack spacing={1}>
                      <StarIcon boxSize={3} color="yellow.500" />
                      <Text fontSize="sm">{place.rating} ({place.rating_count || 0} avaliações)</Text>
                    </HStack>
                  )}

                  {place.website && (
                    <Link href={place.website} isExternal fontSize="sm" color="blue.500">
                      <HStack spacing={1}>
                        <ExternalLinkIcon />
                        <Text>Website</Text>
                      </HStack>
                    </Link>
                  )}
                </HStack>
              </Box>

              <Divider />

              {/* Status e Progresso */}
              <Box>
                <Text fontWeight="semibold" mb={2}>📊 Status</Text>
                <VStack spacing={2} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm">Status atual:</Text>
                    <Badge colorScheme={getStatusColor()}>{getStatusText()}</Badge>
                  </HStack>

                  {place.contacted && (
                    <>
                      <HStack justify="space-between">
                        <Text fontSize="sm">Pessoa de contato:</Text>
                        <Text fontSize="sm" fontWeight="medium">{place.contact_person_name || 'N/A'}</Text>
                      </HStack>

                      <HStack justify="space-between">
                        <Text fontSize="sm">Canal usado:</Text>
                        <HStack>
                          <Text fontSize="sm">{getContactChannelIcon(place.contact_channel)}</Text>
                          <Text fontSize="sm">{getContactChannelText(place.contact_channel)}</Text>
                        </HStack>
                      </HStack>

                      {place.contact_notes && (
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" fontWeight="medium">Notas do contato:</Text>
                          <Text fontSize="sm" color="gray.700" p={2} bg="gray.50" borderRadius="md">
                            {place.contact_notes}
                          </Text>
                        </VStack>
                      )}
                    </>
                  )}

                  {place.converted && (
                    <>
                      <HStack justify="space-between">
                        <Text fontSize="sm">Valor da conversão:</Text>
                        <Text fontSize="sm" fontWeight="bold" color="green.600">
                          {place.conversion_value && typeof place.conversion_value === 'number' ? `R$ ${place.conversion_value.toFixed(2)}` : 'N/A'}
                        </Text>
                      </HStack>

                      {place.conversion_date && (
                        <HStack justify="space-between">
                          <Text fontSize="sm">Data da conversão:</Text>
                          <Text fontSize="sm">{format(new Date(place.conversion_date), "dd/MM/yyyy", { locale: ptBR })}</Text>
                        </HStack>
                      )}

                      {place.conversion_notes && (
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" fontWeight="medium">Notas da conversão:</Text>
                          <Text fontSize="sm" color="gray.700" p={2} bg="green.50" borderRadius="md">
                            {place.conversion_notes}
                          </Text>
                        </VStack>
                      )}
                    </>
                  )}

                  {place.status === "lost" && place.lost_reason && (
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" fontWeight="medium">Motivo da perda:</Text>
                      <Text fontSize="sm" color="gray.700" p={2} bg="red.50" borderRadius="md">
                        {place.lost_reason}
                      </Text>
                    </VStack>
                  )}

                  {isNeedingReturn && (
                    <Box p={3} bg={isReturnOverdue ? "red.50" : "orange.50"} borderRadius="md" border="1px" borderColor={isReturnOverdue ? "red.200" : "orange.200"}>
                      <HStack justify="space-between">
                        <Text fontSize="sm" fontWeight="semibold" color={isReturnOverdue ? "red.700" : "orange.700"}>
                          {isReturnOverdue ? "🚨 Retorno Atrasado" : "⏰ Retorno Agendado"}
                        </Text>
                        <Text fontSize="sm" fontWeight="medium">
                          {format(new Date(place.return_contact_date), "dd/MM/yyyy", { locale: ptBR })}
                        </Text>
                      </HStack>
                    </Box>
                  )}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onDetailsModalClose}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ProspectPlaceCard;